import moment from "moment";

//EL SISTEMA GENERA TODO POR VENCIDO ARRIENDO/REMARCADORES, LA LOGICA DE PAGOS ARRIENDO DA LA ILUSION DE QUE ES ANTICIPADO PERO ES VENCIDO.
// - Históricas : duplica el primer período y corre secuencialmente los montos siguientes.
// - Vigentes : mantiene el comportamiento normal sin perder meses.
// - Futuras : conserva la lógica de vencido/anticipado (duplicado inicial con corrimiento controlado).

const mergeDetailedChargesWithoutDuplicates = (existing, incoming) => {
  const base = existing || [];
  const nuevos = (incoming || []).filter((nuevo) => {
    return !base.some(
      (current) =>
        current.item === nuevo.item &&
        current.servicios === nuevo.servicios &&
        current.idElemento === nuevo.idElemento &&
        current.moneda === nuevo.moneda
    );
  });
  return [...base, ...nuevos];
};

const mergeMonthlyData = (
  combinedMonthlyCharges,
  combinedMonthlyDetailedCharges,
  monthlyCharges,
  monthlyDetailedCharges
) => {
  // Acumula montos por mes y mergea detalles evitando duplicados entre fuentes.
  Object.keys(monthlyCharges).forEach((key) => {
    if (!combinedMonthlyCharges[key]) {
      combinedMonthlyCharges[key] = {
        total_factura_afecta: 0,
        total_factura_exenta: 0,
        total_nota_cobro: 0,
      };
      combinedMonthlyDetailedCharges[key] = [];
    }

    combinedMonthlyCharges[key].total_factura_afecta +=
      monthlyCharges[key].total_factura_afecta;
    combinedMonthlyCharges[key].total_factura_exenta +=
      monthlyCharges[key].total_factura_exenta;
    combinedMonthlyCharges[key].total_nota_cobro +=
      monthlyCharges[key].total_nota_cobro;

    combinedMonthlyDetailedCharges[key] = mergeDetailedChargesWithoutDuplicates(
      combinedMonthlyDetailedCharges[key],
      monthlyDetailedCharges[key]
    );
  });
};

const patchConsolidationFields = (items, finalitems) =>
  items.map((item) => {
    const updated = finalitems.find((f) => f.id === item.id);
    return updated
      ? {
          ...item,
          consolidado: updated.consolidado,
          agrupamiento: updated.agrupamiento,
        }
      : item;
  });

const validarDisponibilidad = async ({
  baseUrl,
  formattedAllItems,
  alertFn = alert,
}) => {
  // Valida en backend que los productos no estén reservados en el mismo rango de fechas.
  try {
    const payload = formattedAllItems
      .filter((item) => item.relacion === "producto")
      .map((item) => ({
        idProducto: item.id_elemento,
        fechaInicio: item.desde,
        fechaTermino: item.hasta,
      }));

    const response = await fetch(`${baseUrl}validarDisponibilidad`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (data.respuesta) return true;

    const detalles = (data.productosReservados || [])
      .map(
        (producto) =>
          `Producto: ${producto.idProducto}\n` +
          `Fechas reservadas:\n${(producto.detallesReservados || [])
            .map(
              (detalle) =>
                `- ${detalle.descripcion}, Cliente: ${detalle.razon_social}, Fechas: ${detalle.fecha_inicio} a ${detalle.fecha_termino}`
            )
            .join("\n")}`
      )
      .join("\n\n");

    alertFn(
      `Uno o más productos ya fueron arrendados. Detalles:\n\n${detalles}\n\nPor favor vuelva a generar la cotización.`
    );
    return false;
  } catch (error) {
    alertFn("Uno o más productos se encuentran inhabilitados.");
    console.error("Error al validar disponibilidad:", error);
    return false;
  }
};

const crearSubproductos = async ({
  allItems,
  buildInsertSubproductoUrl,
}) => {
  // Inserta subproductos ligados al producto padre; resuelve el vínculo por id o por descripción.
  const productosIndexByDesc = {};
  allItems.forEach((it) => {
    if (it.relacion === "producto") {
      productosIndexByDesc[it.descripcion] = it;
    }
  });

  const subproductosToCreate = allItems.filter(
    (it) => it.relacion === "subproducto"
  );
  if (subproductosToCreate.length === 0) return;

  const requests = subproductosToCreate.map((sub) => {
    const idEmpresa = sub.id_empresa;
    const idProducto =
      sub.id_elemento_producto ||
      (() => {
        const parts = String(sub.descripcion).split(" - ");
        const prodDesc = parts.length > 1 ? parts[1] : null;
        return prodDesc && productosIndexByDesc[prodDesc]
          ? productosIndexByDesc[prodDesc].id_elemento
          : null;
      })();

    const idTipoSubproducto = sub.id_elemento;
    const metraje = sub.cantidad || 0;
    if (!idProducto) return Promise.resolve();

    const url = buildInsertSubproductoUrl(
      idEmpresa,
      idProducto,
      idTipoSubproducto,
      metraje
    );
    return fetch(url, { method: "POST", redirect: "follow" });
  });

  await Promise.all(requests);
};

const upsertContrato = async ({
  idCotizacion,
  buildConsultarContratoUrl,
  buildActualizarContratoUrl,
  buildCrearContratoUrl,
  alertFn = alert,
}) => {
  // Si ya existe contrato para la cotización lo actualiza, si no existe lo crea.
  try {
    const res = await fetch(buildConsultarContratoUrl(idCotizacion));
    if (!res.ok) throw new Error(`Consulta fallida: ${res.status}`);
    const { contrato } = await res.json();

    if (contrato) {
      const actualizarRes = await fetch(
        buildActualizarContratoUrl(idCotizacion, contrato.id),
        { method: "PUT" }
      );
      if (!actualizarRes.ok) throw new Error("Error al actualizar el contrato");
      return;
    }

    const crearRes = await fetch(buildCrearContratoUrl(idCotizacion), {
      method: "POST",
    });
    if (!crearRes.ok) throw new Error("Error al crear el contrato");
  } catch (error) {
    console.error(error);
    alertFn("Ocurrió un error al verificar o crear el contrato");
  }
};

export const ejecutarCierreContrato = async ({
  finalitems,
  baseUrl,
  urlContratos,
  idCotizacion,
  idCliente,
  cotizacion,
  diasAviso,
  diasFirma,
  flattenedDataArriendo,
  servicios,
  serviciosUnicos,
  formatDatesIfNeeded,
  guardarCotizacion,
  buildInsertSubproductoUrl,
  buildConsultarContratoUrl,
  buildActualizarContratoUrl,
  buildCrearContratoUrl,
  calculateMonthlyChargesByType,
  savePeriodoFacturacionArray,
  formatDate,
  updateCotizacion,
  updatePlazoFirma,
  navigate,
  cobroArriendoEnPeriodoActual = true,
  alertFn = alert,
}) => {
  // Orquestador principal: valida, persiste cotización/contrato, recalcula facturación y navega con toasts.
  const allItems = finalitems;
  const formattedAllItems = allItems.map((item) => formatDatesIfNeeded(item, false));

  const isAvailable = await validarDisponibilidad({
    baseUrl,
    formattedAllItems,
    alertFn,
  });
  if (!isAvailable) return { aborted: true };

  await guardarCotizacion(allItems);
  await crearSubproductos({
    allItems,
    buildInsertSubproductoUrl,
  });

  await upsertContrato({
    idCotizacion,
    buildConsultarContratoUrl,
    buildActualizarContratoUrl,
    buildCrearContratoUrl,
    alertFn,
  });

  const combinedMonthlyCharges = {};
  const combinedMonthlyDetailedCharges = {};

  const chargesArrays = [flattenedDataArriendo, servicios].map((group) =>
    patchConsolidationFields(group, finalitems)
  );
  const uniqueServiceCharges = patchConsolidationFields(
    serviciosUnicos,
    finalitems
  );

  chargesArrays.forEach((charges) => {
    const { monthlyCharges, monthlyDetailedCharges } =
      calculateMonthlyChargesByType(charges, false);
    mergeMonthlyData(
      combinedMonthlyCharges,
      combinedMonthlyDetailedCharges,
      monthlyCharges,
      monthlyDetailedCharges
    );
  });

  const monthlyUniqueCharges = calculateMonthlyChargesByType(
    uniqueServiceCharges,
    true
  );
  mergeMonthlyData(
    combinedMonthlyCharges,
    combinedMonthlyDetailedCharges,
    monthlyUniqueCharges.monthlyCharges,
    monthlyUniqueCharges.monthlyDetailedCharges
  );

  const {
    monthlyCharges: arriendoMonthlyCharges,
    monthlyDetailedCharges: arriendoMonthlyDetails,
  } = calculateMonthlyChargesByType(flattenedDataArriendo, false);

  const validKeys = Object.keys(combinedMonthlyCharges)
    .filter((key) => /^[0-9]{4}-[0-9]{2}$/.test(key))
    .sort();
  const mesInicio = moment().subtract(1, "month").format("YYYY-MM");
  const mesActual = moment().format("YYYY-MM");
  // ------------- se construye la lista de meses a incluir ---------------
  const keysFiltradas = validKeys.filter((key) => key >= mesInicio);
  const isHistorica = validKeys.length > 0 && validKeys[0] < mesInicio;
  const isVigente =
    !isHistorica &&
    validKeys.length > 0 &&
    validKeys[0] <= mesActual &&
    keysFiltradas.length > 0;
  const isFutura = !isHistorica && !isVigente;
  const allValidKeysFiltrados = isHistorica
    ? [...validKeys]
    : keysFiltradas.length > 0
      ? keysFiltradas
      : [...validKeys];
  console.log("allValidKeysFiltrados", allValidKeysFiltrados);

  const fechaCreacion = moment(cotizacion.fecha_creacion_cotizacion).format(
    "YYYY-MM"
  );

  const firstFacturableKey = validKeys[0] || fechaCreacion;
  const isFutureCotizacion = moment(firstFacturableKey, "YYYY-MM").isAfter(
    moment(fechaCreacion, "YYYY-MM")
  );
  const shouldInjectCurrentPeriod =
    !isFutureCotizacion || cobroArriendoEnPeriodoActual;

  let firstMonthKey = firstFacturableKey;
  const injectionTargetKey = isHistorica
    ? firstFacturableKey
    : moment
        .min(moment(fechaCreacion, "YYYY-MM"), moment(firstFacturableKey, "YYYY-MM"))
        .format("YYYY-MM");

  if (isHistorica && shouldInjectCurrentPeriod) {
    // Históricas: duplica primer período real y luego aplica corrimiento secuencial.
    allValidKeysFiltrados.unshift(firstFacturableKey);
    firstMonthKey = firstFacturableKey;
  } else if (isVigente && shouldInjectCurrentPeriod && validKeys.length > 0) {
    // Vigentes: duplica primer período facturable sin lógica histórica ni inyección.
    allValidKeysFiltrados.unshift(firstFacturableKey);
    firstMonthKey = firstFacturableKey;
  } else if (isFutura && !shouldInjectCurrentPeriod && validKeys.length > 0) {
    // Futuras sin cobro en periodo actual: duplicar primer mes facturable
    // para separar vencido (primer registro) y anticipado (segundo registro).
    allValidKeysFiltrados.unshift(firstFacturableKey);
    firstMonthKey = firstFacturableKey;
  } else if (isFutura && shouldInjectCurrentPeriod && validKeys.length > 0) {
    // Futuras con cobro en periodo actual: inyecta período actual mínimo.
    allValidKeysFiltrados.unshift(injectionTargetKey);
    firstMonthKey = injectionTargetKey;
  }

  if (
    isFutura &&
    shouldInjectCurrentPeriod &&
    !combinedMonthlyCharges[injectionTargetKey]
  ) {
    const sourceKey = combinedMonthlyCharges[injectionTargetKey]
      ? injectionTargetKey
      : validKeys[0] || null;
    const sourceCombined = sourceKey ? combinedMonthlyCharges[sourceKey] : null;
    const sourceArriendo = sourceKey ? arriendoMonthlyCharges[sourceKey] : null;
    const sourceDetails = sourceKey
      ? combinedMonthlyDetailedCharges[sourceKey] || arriendoMonthlyDetails[sourceKey] || []
      : [];

    combinedMonthlyCharges[injectionTargetKey] = {
      total_factura_afecta:
        sourceCombined?.total_factura_afecta || sourceArriendo?.total_factura_afecta || 0,
      total_factura_exenta:
        sourceCombined?.total_factura_exenta || sourceArriendo?.total_factura_exenta || 0,
      total_nota_cobro:
        sourceCombined?.total_nota_cobro || sourceArriendo?.total_nota_cobro || 0,
    };
    combinedMonthlyDetailedCharges[injectionTargetKey] = sourceDetails;
  }
  
  // ------------- se crea cada período ---------------
  const periodoFacturacionArray = allValidKeysFiltrados.map((key, index, array) => {
    // Históricas y vigentes: al duplicar el primer período, desde la segunda fila
    // los montos se corren secuencialmente al siguiente mes disponible.
    const hasInitialDuplicate =
      array.length > 1 && array[0] === firstMonthKey && array[1] === firstMonthKey;
    const applySequentialShift =
      hasInitialDuplicate && (isHistorica || isVigente) && index > 0;

    // Futuras sin periodo actual: solo la segunda fila del primer mes duplicado se corre.
    const isSecondInitialMonth =
      index > 0 && key === firstMonthKey && array[index - 1] === firstMonthKey;
    const applyAdvanceShift = !shouldInjectCurrentPeriod && isSecondInitialMonth;

    // Futuras con cobro en periodo actual: evita duplicar montos en el período siguiente.
    const applyFutureCurrentShift =
      isFutura &&
      shouldInjectCurrentPeriod &&
      firstMonthKey !== firstFacturableKey &&
      index > 0 &&
      key === firstFacturableKey;

    const sourceChargeKey = applySequentialShift
      ? array[index + 1] || key
      : applyAdvanceShift || applyFutureCurrentShift
        ? array.slice(index + 1).find((nextKey) => nextKey !== key) || key
        : key;

    const monthlySource =
      combinedMonthlyCharges[sourceChargeKey] || combinedMonthlyCharges[key] || {
        total_factura_afecta: 0,
        total_factura_exenta: 0,
        total_nota_cobro: 0,
      };

    const detailsSource =
      combinedMonthlyDetailedCharges[sourceChargeKey] ||
      combinedMonthlyDetailedCharges[key] ||
      [];

    return {
      idCotizacion,
      periodo: key,
      vencimiento: moment(key, "YYYY-MM").endOf("month").format("YYYY-MM-DD"),
      totalFacturaAfecta: monthlySource.total_factura_afecta,
      totalFacturaExenta: monthlySource.total_factura_exenta,
      totalNotaCobro: monthlySource.total_nota_cobro,
      estado: "emitido",
      idEjecutor: null,
      idValidador: null,
      detallesCobro: detailsSource,
    };
  });
  console.log("periodoFacturacionArray", periodoFacturacionArray);

  // ------------- se persiste cada período ---------------
  await savePeriodoFacturacionArray(periodoFacturacionArray);

  const fechasDesde = formattedAllItems.map((item) =>
    moment(item.desde, "YYYY-MM-DD")
  );
  const fechasHasta = formattedAllItems.map((item) =>
    moment(item.hasta, "YYYY-MM-DD")
  );
  const fechaMin = moment.min(fechasDesde);
  const fechaMax = moment.max(fechasHasta);
  const desdeTransformed = formatDate(fechaMin.format("YYYY-MM-DD"));
  const hastaTransformed = formatDate(fechaMax.format("YYYY-MM-DD"));

  await updateCotizacion(
    idCotizacion,
    idCliente,
    5,
    sessionStorage.getItem("usuario_id"),
    desdeTransformed,
    hastaTransformed,
    diasAviso
  );

  const contratoResp = await fetch(
    `${baseUrl}getContratoByCotizacionId?idCotizacion=${idCotizacion}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    }
  );
  if (!contratoResp.ok) {
    throw new Error("No se pudo obtener el contrato por cotización");
  }
  const contratoJson = await contratoResp.json();
  const contrato = contratoJson?.contrato;
  if (!contrato || !contrato.id || !contrato.idEmpresa) {
    throw new Error("Respuesta de contrato inválida o incompleta");
  }

  const plantillaResp = await fetch(
    `${urlContratos}getPlantillaContrato?idEmpresa=${contrato.idEmpresa}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    }
  );
  if (!plantillaResp.ok) {
    throw new Error("No se pudo obtener la plantilla de contrato");
  }
  const plantillaText = await plantillaResp.text();
  const idPlantilla = Number(plantillaText.trim());
  if (!Number.isFinite(idPlantilla) || idPlantilla <= 0) {
    throw new Error(`idPlantilla inválido: "${plantillaText}"`);
  }

  await updatePlazoFirma(contrato.id, diasFirma);

  const metadataResp = await fetch(
    `${baseUrl}getMetadataByCotizacionId?idCotizacion=${idCotizacion}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    }
  );
  if (!metadataResp.ok) {
    throw new Error("No se pudo obtener la metadata por cotización");
  }
  const metadataJson = await metadataResp.json();

  const payloadContratoDocumento = {
    idEmpresa: contrato.idEmpresa,
    idPlantilla,
    idContrato: contrato.id,
    idUsuario: Number(sessionStorage.getItem("usuario_id") || 0),
    idEstadoDocumento: 1,
    filename: `contrato ${idCotizacion}.docx`,
    metadataJson: metadataJson || {},
    bypass: true,
  };

  const guardarResp = await fetch(`${urlContratos}guardarMetadataDocumento`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payloadContratoDocumento),
    credentials: "include",
  });

  navigate("/asignacion", {
    state: {
      toast: [
        {
          severity: "success",
          summary: "Éxito",
          detail: "La cotización ha sido asignada con éxito",
          life: 3000,
        },
        guardarResp.ok
          ? {
              severity: "success",
              summary: "Éxito",
              detail: "El contrato se ha creado con éxito",
              life: 3000,
            }
          : {
              severity: "error",
              summary: "Error",
              detail: "Error al crear el contrato",
              life: 3000,
            },
      ],
    },
  });

  return { aborted: false };
};
